import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Receipt } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuthStore } from '../../stores/authStore';
import {
  useActiveIncomeDepartments,
  useActiveAccounts,
  useCreateIncome,
} from '../../hooks/useQueries';
import { printIncomeReceipt } from '../../lib/printReceipt';
import { verifyPosPrinterReady } from '../../lib/posPrinterGate';
import { getLastFormDefault, setLastFormDefault } from '../../lib/formDefaults';
import { IncomeDepartmentsAPI } from '../../lib/sdk/index';
import type { IncomeType } from '../../lib/sdk/index';
import { getErrorMessage } from '../../lib/utils/errorMessage';

interface FormData {
  departmentId: string;
  incomeTypeId: string;
  amount: string;
  accountId: string;
  notes: string;
}

const AddIncome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: departmentsData = [], isLoading: deptsLoading } = useActiveIncomeDepartments();
  const { data: accountsData = [], isLoading: accountsLoading } = useActiveAccounts();

  const departments = Array.isArray(departmentsData) ? departmentsData : [];
  const accounts = Array.isArray(accountsData) ? accountsData : [];
  const dataLoading = deptsLoading || accountsLoading;

  // Income types cascade — loaded after department selection
  const [availableTypes, setAvailableTypes] = useState<IncomeType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // Print state
  const [isPrinting, setIsPrinting] = useState(false);
  const [posPrinterAvailable, setPosPrinterAvailable] = useState(false);
  const [printerStatusMessage, setPrinterStatusMessage] = useState('');
  const printerPollPausedRef = useRef(false);

  const [electronAvailable, setElectronAvailable] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    departmentId: '',
    incomeTypeId: '',
    amount: '',
    accountId: '',
    notes: '',
  });

  const createIncomeMutation = useCreateIncome();

  useEffect(() => {
    setElectronAvailable(!!window.electronAPI);
  }, []);

  // Poll printer availability
  useEffect(() => {
    if (electronAvailable) {
      checkPrinterAvailability();
      const interval = setInterval(() => {
        if (!printerPollPausedRef.current) {
          checkPrinterAvailability();
        }
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [electronAvailable]);

  const checkPrinterAvailability = async () => {
    const gate = await verifyPosPrinterReady();
    setPosPrinterAvailable(gate.ready);
    setPrinterStatusMessage(gate.ready ? (gate.printerName || 'Connected') : gate.message);
  };

  // Default to cash account
  useEffect(() => {
    if (accounts.length > 0 && !formData.accountId) {
      const cashAccount = accounts.find((acc: any) => acc.isCashAccount);
      if (cashAccount) {
        setFormData((prev) => ({ ...prev, accountId: cashAccount._id }));
      }
    }
  }, [accounts, formData.accountId]);

  // Restore last-used department and income type
  useEffect(() => {
    if (departments.length === 0 || formData.departmentId) return;

    const lastDeptId = getLastFormDefault('lastIncomeDepartmentId');
    if (!lastDeptId || !departments.some((d: any) => d._id === lastDeptId)) return;

    const restore = async () => {
      setFormData((prev) => ({ ...prev, departmentId: lastDeptId }));
      setTypesLoading(true);
      try {
        const types = await IncomeDepartmentsAPI.getDepartmentTypes(lastDeptId);
        const activeTypes = (types || []).filter((t: IncomeType) => t.isActive !== false);
        setAvailableTypes(activeTypes);
        const lastTypeId = getLastFormDefault('lastIncomeTypeId');
        if (lastTypeId && activeTypes.some((t) => t._id === lastTypeId)) {
          setFormData((prev) => ({ ...prev, departmentId: lastDeptId, incomeTypeId: lastTypeId }));
        }
      } catch {
        // ignore — user can select manually
      } finally {
        setTypesLoading(false);
      }
    };

    void restore();
  }, [departments, formData.departmentId]);

  // Load income types when department changes
  const handleDepartmentChange = async (departmentId: string) => {
    setFormData((prev) => ({ ...prev, departmentId, incomeTypeId: '' }));
    setAvailableTypes([]);
    if (!departmentId) return;
    setTypesLoading(true);
    try {
      const types = await IncomeDepartmentsAPI.getDepartmentTypes(departmentId);
      const active = Array.isArray(types) ? types.filter((t: any) => t.isActive !== false) : [];
      setAvailableTypes(active);
    } catch (err) {
      console.error('Failed to load department types', err);
      toast.error('Failed to load income types for this department');
    } finally {
      setTypesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isPrinting || createIncomeMutation.isLoading) return;

    if (!formData.departmentId) { toast.error('Department is required'); return; }
    if (!formData.incomeTypeId) { toast.error('Income type is required'); return; }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (!formData.accountId) { toast.error('Account is required'); return; }

    if (!electronAvailable) {
      toast.error('Income can only be recorded in the HRIA Desktop app with a connected POS printer.', { autoClose: 10000 });
      return;
    }

    const printerGate = await verifyPosPrinterReady();
    if (!printerGate.ready) {
      setPosPrinterAvailable(false);
      setPrinterStatusMessage(printerGate.message);
      toast.error(printerGate.message, { autoClose: 10000 });
      return;
    }
    setPosPrinterAvailable(true);
    setPrinterStatusMessage(printerGate.printerName || 'Connected');

    setIsPrinting(true);
    printerPollPausedRef.current = true;

    try {
      const incomeData = {
        departmentId: formData.departmentId,
        incomeTypeId: formData.incomeTypeId,
        amount: parseFloat(formData.amount),
        accountId: formData.accountId,
        notes: formData.notes.trim() || undefined,
        pcInfo: {
          pcName: navigator.platform,
          pcId: `PC_${Date.now()}`,
        },
      };

      // Step 1: Save to DB — server assigns receiptId + verificationCode
      const apiResponse = await createIncomeMutation.mutateAsync(incomeData);
      // API returns { success, data: Income } or the income object directly
      const response = (apiResponse as any)?.data ?? apiResponse;
      if (!response || !response._id) {
        throw new Error('Invalid server response — income not created properly');
      }

      const savedIncomeId = response._id;

      if (response.receiptId === undefined || response.receiptId === null) {
        throw new Error('Server did not return a receipt ID — cannot print receipt');
      }

      const dept = departments.find((d: any) => d._id === formData.departmentId);
      const incomeType = availableTypes.find((t) => t._id === formData.incomeTypeId);
      const account = accounts.find((a: any) => a._id === formData.accountId);

      const receiptPayload = {
        incomeId: response.incomeId ?? response._id,
        receiptId: response.receiptId,
        verificationCode: response.verificationCode,
        amount: response.amount ?? parseFloat(formData.amount),
        departmentName: response.departmentName ?? dept?.name ?? '',
        incomeTypeName: response.incomeTypeName ?? incomeType?.name ?? '',
        accountName: response.accountName ?? account?.name ?? '',
        operatorName: response.operatorName ?? user?.name ?? '',
      };

      // Step 2: Mandatory print — rollback on failure
      try {
        await printIncomeReceipt(receiptPayload, { mandatory: true });
      } catch (printError: unknown) {
        const printMessage = getErrorMessage(printError, 'Print failed');
        console.error('[ATOMIC] Print failed — rolling back income:', printError);
        try {
          await (await import('../../lib/sdk/index')).IncomesAPI.deleteIncome(savedIncomeId);
          console.log('[ATOMIC] Rollback succeeded — income deleted');
        } catch (deleteError) {
          console.error('[ATOMIC] Rollback failed — income remains in DB:', deleteError);
          throw new Error(
            `Income #${receiptPayload.receiptId} was saved but printing failed: ${printMessage}. ` +
            'Reprint from Manage Incomes, or delete it there if it should not stand.'
          );
        }
        throw new Error(`Receipt printing failed: ${printMessage}. The income was not saved.`);
      }

      if (formData.departmentId) {
        setLastFormDefault('lastIncomeDepartmentId', formData.departmentId);
      }
      if (formData.incomeTypeId) {
        setLastFormDefault('lastIncomeTypeId', formData.incomeTypeId);
      }

      toast.success(`Income #${receiptPayload.receiptId} recorded and receipt printed.`);

      // Stay on this page for fast back-to-back entry: keep department/type/account,
      // clear only the per-record fields.
      setFormData((prev) => ({ ...prev, amount: '', notes: '' }));
    } catch (err: any) {
      console.error('Error creating income:', err);
      toast.error(getErrorMessage(err, 'Failed to create income'));
    } finally {
      setIsPrinting(false);
      printerPollPausedRef.current = false;
      if (electronAvailable) {
        setTimeout(() => {
          checkPrinterAvailability();
        }, 2000);
      }
    }
  };

  if (dataLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
          <TrendingUp className="h-8 w-8 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Add Income</h1>
          <p className="text-gray-600 dark:text-gray-400">Record organizational income for Central Office</p>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Income Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Income Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Department */}
              <div>
                <label className="form-label">Department *</label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select department</option>
                  {departments.map((dept: any) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}{dept.code ? ` (${dept.code})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Income Type — cascades from department */}
              <div>
                <label className="form-label">Income Type *</label>
                <select
                  value={formData.incomeTypeId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, incomeTypeId: e.target.value }))}
                  className="form-select"
                  required
                  disabled={!formData.departmentId || typesLoading}
                >
                  <option value="">
                    {!formData.departmentId
                      ? 'Select department first'
                      : typesLoading
                      ? 'Loading types...'
                      : availableTypes.length === 0
                      ? 'No types assigned to this department'
                      : 'Select income type'}
                  </option>
                  {availableTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {formData.departmentId && !typesLoading && availableTypes.length === 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                    No income types assigned. Go to Income Departments to assign types.
                  </p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="form-label">Amount *</label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  min="0"
                  step="1"
                  required
                />
              </div>

              {/* Account */}
              <div>
                <label className="form-label">Account *</label>
                <select
                  value={formData.accountId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, accountId: e.target.value }))}
                  className="form-select"
                  required
                >
                  <option value="">Select account</option>
                  {accounts.map((account: any) => (
                    <option key={account._id} value={account._id}>
                      {account.name} ({account.accountNumber}) — Rs. {account.balance.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Additional Info</h3>
            <div>
              <label className="form-label">Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
                maxLength={500}
                className="form-select resize-none"
              />
            </div>
          </div>

          {/* Transaction Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Transaction Details</h3>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Operator:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{user?.name || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Role:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{user?.role || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Time:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Printer status */}
          {electronAvailable && (
            <div className="pt-2">
              {posPrinterAvailable ? (
                <div className="text-sm text-green-600 dark:text-green-400">
                  ✓ POS printer connected{printerStatusMessage && printerStatusMessage !== 'Connected' ? ` (${printerStatusMessage})` : ''}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="text-sm text-red-600 dark:text-red-400">
                    ⚠ {printerStatusMessage || 'POS printer not connected — check USB/power and Settings → Printer'}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      toast.info('Rechecking printer connection...');
                      await checkPrinterAvailability();
                      if (posPrinterAvailable) toast.success('Printer connected!');
                      else toast.info('No POS printer found. Configure one in Settings → Printer.');
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 underline whitespace-nowrap"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
            <Button type="button" variant="secondary" onClick={() => navigate('/incomes')} disabled={isPrinting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPrinting || !electronAvailable || !posPrinterAvailable}
              leftIcon={isPrinting ? undefined : <Receipt className="h-4 w-4" />}
            >
              {isPrinting ? 'Processing...' : 'Create Income'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddIncome;
